import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, m } from 'motion/react';
import {
  Archive,
  ArchiveRestore,
  CalendarClock,
  CreditCard,
  MoreVertical,
  Pencil,
  Plus,
  ReceiptText,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import {
  CARD_BRAND_LABELS,
  formatBRL,
  formatDateBR,
  type CardsResponse,
  type CreditCard as CreditCardModel,
} from '@lira/shared';
import { apiGet, apiPatch } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useMonthStore } from '@/stores/month';
import { useComposeStore } from '@/stores/compose';
import { toast } from '@/stores/toast';
import {
  BRANDS,
  CARD_BRAND_TO_SLUG,
  CategoryIcon,
  EmptyState,
  MoneyText,
  Skeleton,
  brandIconKey,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/layout/PageHeader';
import { CardFormModal } from './CardFormModal';
import { PayInvoiceModal } from './PayInvoiceModal';
import { STATUS_META } from './cardMeta';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Logo/cor do banco (círculo) + bandeirinha da rede no canto (Visa/Master/Elo…). */
function BrandBadge({ card }: { card: CreditCardModel }) {
  const netSlug = CARD_BRAND_TO_SLUG[card.brand];
  const net = netSlug ? BRANDS[netSlug] : undefined;
  return (
    <span className="relative shrink-0">
      <CategoryIcon icon={card.icon} color={card.color} fallbackText={card.name} size={40} />
      {net && (
        <span className="absolute -bottom-1 -right-1 rounded-full bg-surface p-0.5 shadow-card">
          <CategoryIcon icon={brandIconKey(netSlug!)} color={net.hex} size={16} />
        </span>
      )}
    </span>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-canvas"
    >
      <Icon className="size-4 shrink-0 text-muted" />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function CardMenu({
  card,
  onEdit,
  onHistory,
}: {
  card: CreditCardModel;
  onEdit: () => void;
  onHistory: () => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Só Escape aqui; o descarte por clique-fora usa um backdrop (abaixo). O card
  // é clicável (overlay), então um listener de pointerdown no document deixaria
  // o mesmo clique de descarte também abrir a fatura — o backdrop absorve.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const archive = useMutation({
    mutationFn: () => apiPatch(`/cards/${card.id}/archive`, { archived: !card.archived }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast(card.archived ? 'Cartão desarquivado.' : 'Cartão arquivado.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Ações do cartão"
        className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink focus-visible:ring-2 focus-visible:ring-cards"
      >
        <MoreVertical className="size-5" />
      </button>

      {/* Backdrop de descarte: acima do overlay clicável do card (z-10), abaixo
          do menu (z-30) → clicar fora fecha sem abrir a fatura. */}
      {open && (
        <div
          className="fixed inset-0 z-10"
          aria-hidden
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
        />
      )}

      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15, ease: EASE }}
            role="menu"
            className="absolute right-0 top-full z-30 mt-1 min-w-56 overflow-hidden rounded-2xl border border-border bg-surface! py-1 shadow-hover ring-1 ring-black/5 dark:ring-white/10"
          >
            <MenuItem
              icon={Pencil}
              label="Editar"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            />
            <MenuItem
              icon={ReceiptText}
              label="Histórico de faturas"
              onClick={() => {
                setOpen(false);
                onHistory();
              }}
            />
            <MenuItem
              icon={card.archived ? ArchiveRestore : Archive}
              label={card.archived ? 'Desarquivar' : 'Arquivar'}
              onClick={() => {
                setOpen(false);
                archive.mutate();
              }}
            />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Card tracejado "Novo cartão". */
function NewCardTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hidden min-h-[17.5rem] flex-col items-center justify-center gap-4 rounded-[var(--radius-card)] bg-surface p-5 text-center shadow-card transition-colors hover:bg-cards/5 hover:shadow-hover sm:flex"
    >
      <span className="grid size-16 place-items-center rounded-full border-2 border-cards bg-surface text-cards">
        <Plus className="size-7" />
      </span>
      <span className="text-base font-semibold text-cards">Novo cartão de crédito</span>
    </button>
  );
}

function CardTile({
  card,
  onOpen,
  onEdit,
  onPay,
}: {
  card: CreditCardModel;
  onOpen: () => void;
  onEdit: () => void;
  onPay: () => void;
}) {
  const { invoice } = card;
  const status = STATUS_META[invoice.status];
  const usedPct =
    card.limitCents > 0 ? Math.min(100, Math.max(0, (card.usedCents / card.limitCents) * 100)) : 0;
  const remaining = invoice.remainingCents > 0;
  const isOverdue = invoice.status === 'overdue';

  // Card clicável (consistência "número leva à origem"): o corpo abre a fatura
  // do cartão. Camada-alvo cobre o card (z-0); conteúdo pointer-events-none;
  // controles (⋮ e "Pagar fatura") reativam os eventos com pointer-events-auto.
  return (
    <div data-testid="credit-card-tile" className="group relative flex min-h-[17.5rem] min-w-0 flex-col rounded-[var(--radius-card)] bg-surface p-5 shadow-card transition-shadow hover:shadow-hover">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Abrir fatura de ${card.name}`}
        className="absolute inset-0 z-0 rounded-[var(--radius-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cards"
      />

      {/* header */}
      <div className="pointer-events-none relative z-10 flex items-start gap-3">
        <BrandBadge card={card} />
        <div className="min-w-0 flex-1 pt-0.5">
          <span className="block truncate font-semibold text-ink group-hover:text-cards">
            {card.name}
            {card.archived && (
              <span className="ml-2 rounded-full bg-canvas px-2 py-0.5 text-[11px] font-medium text-muted">
                Arquivado
              </span>
            )}
          </span>
          <span className="block truncate text-[12px] text-muted">
            {CARD_BRAND_LABELS[card.brand]}
          </span>
        </div>
        <div className="pointer-events-auto">
          <CardMenu card={card} onEdit={onEdit} onHistory={onOpen} />
        </div>
      </div>

      {/* status + valor da fatura */}
      <div className="pointer-events-none relative z-10 mt-3">
        <span
          className={cn(
            'inline-block rounded-full px-2.5 py-0.5 text-[12px] font-semibold',
            status.cls,
          )}
        >
          {status.label}
        </span>
        <div className="mt-2 flex items-baseline justify-between gap-2">
          <MoneyText
            cents={invoice.totalCents}
            bold
            className={cn('text-2xl', remaining && isOverdue && 'text-alert!')}
          />
          <span className="text-[12px] text-muted">
            {invoice.dueDate ? `Vence em ${formatDateBR(invoice.dueDate)}` : ''}
          </span>
        </div>
        {remaining && invoice.paidCents > 0 && (
          <p className="mt-0.5 text-[12px] text-muted">
            Pago {formatBRL(invoice.paidCents)} · Restam{' '}
            <span className="font-semibold text-ink">{formatBRL(invoice.remainingCents)}</span>
          </p>
        )}
        {invoice.totalCents === 0 && (
          <p className="mt-0.5 text-[12px] text-muted">Sem lançamentos nesta fatura.</p>
        )}
      </div>

      {/* barra de uso do limite */}
      <div className="pointer-events-none relative z-10 mt-3">
        <div className="h-2 w-full overflow-hidden rounded-full bg-canvas">
          <div className="h-full rounded-full bg-cards" style={{ width: `${usedPct}%` }} />
        </div>
        <div className="mt-1 flex items-center justify-between text-[12px] text-muted">
          <span>
            {formatBRL(card.usedCents)} usado de {formatBRL(card.limitCents)}
          </span>
          <span aria-label={`${usedPct.toFixed(2)}% do limite utilizado`}>
            {usedPct.toFixed(2)}%
          </span>
        </div>
        <p className="mt-0.5 text-[12px] text-muted">
          Limite disponível{' '}
          <span className="font-semibold text-ink">{formatBRL(card.availableLimitCents)}</span>
        </p>
      </div>

      {/* rodapé */}
      {remaining && (
        <div className="pointer-events-none relative z-10 mt-auto border-t border-border pt-3">
          <button
            type="button"
            onClick={onPay}
            className="pointer-events-auto text-xs font-semibold uppercase tracking-wide text-cards transition-opacity hover:opacity-80"
          >
            Pagar fatura
          </button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  children,
  className,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col items-start gap-2 rounded-[var(--radius-panel)] bg-surface p-3 shadow-card sm:flex-row sm:items-center sm:gap-3 sm:p-4',
        className,
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-cards/10 text-cards">
        <Icon className="size-5" />
      </span>
      <div className="min-w-0 max-w-full">
        <p className="truncate text-[13px] text-muted">{label}</p>
        <div className="mt-0.5 text-lg font-bold text-ink">{children}</div>
      </div>
    </div>
  );
}

export function CardsPage() {
  const month = useMonthStore((s) => s.month);
  const navigate = useNavigate();
  const openCardExpense = useComposeStore((s) => s.openCardExpense);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CreditCardModel | undefined>(undefined);
  const [paying, setPaying] = useState<CreditCardModel | null>(null);

  const query = useQuery({
    queryKey: ['cards', month],
    queryFn: () => apiGet<CardsResponse>(`/cards?month=${month}`),
  });

  const openNew = () => {
    setEditing(undefined);
    setFormOpen(true);
  };
  const openEdit = (card: CreditCardModel) => {
    setEditing(card);
    setFormOpen(true);
  };

  const cards = query.data?.cards ?? [];
  const isEmpty = query.isSuccess && cards.length === 0;

  return (
    <section className="space-y-6">
      <PageHeader
        title="Cartões de crédito"
        subtitle="Faturas, vencimentos e limites no período selecionado."
        accent="cards"
        actions={
          <>
            <button
              type="button"
              onClick={() => openCardExpense()}
              aria-label="Lançar gasto no cartão de crédito"
              title="Lançar gasto no cartão de crédito"
              className="grid size-11 shrink-0 place-items-center rounded-full bg-cards text-white shadow-card transition-colors hover:bg-cards/90"
            >
              <Plus className="size-5" />
            </button>
            <button
              type="button"
              onClick={openNew}
              className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-full border border-cards px-3 text-sm font-semibold text-cards transition-colors hover:bg-cards/10 sm:hidden"
            >
              <CreditCard className="size-4" />
              <span className="hidden min-[360px]:inline">Novo cartão</span>
            </button>
          </>
        }
      />

      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_19rem]">
        {/* coluna principal: grid de cartões */}
        <div>
          {query.isLoading && (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <Skeleton key={i} variant="card" className="min-h-[220px]" />
              ))}
            </div>
          )}

          {query.isError && (
            <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
              {errorMessage(query.error)}
            </p>
          )}

          {isEmpty && (
            <EmptyState
              icon={CreditCard}
              title="Nenhum cartão ainda"
              description="Cadastre seus cartões de crédito para acompanhar faturas, limite e parcelamentos."
            >
              <button
                type="button"
                onClick={openNew}
                className="inline-flex h-11 items-center gap-2 rounded-full bg-cards px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90"
              >
                <Plus className="size-5" />
                Novo cartão
              </button>
            </EmptyState>
          )}

          {query.isSuccess && cards.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <NewCardTile onClick={openNew} />
              {cards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  onOpen={() => navigate(`/cartoes/${card.id}`)}
                  onEdit={() => openEdit(card)}
                  onPay={() => setPaying(card)}
                />
              ))}
            </div>
          )}
        </div>

        {/* coluna direita: 3 resumos teal */}
        <aside
          data-testid="cards-summary"
          className="grid min-w-0 grid-cols-2 gap-3 xl:sticky xl:top-[5.5rem] xl:grid-cols-1 xl:self-start"
        >
          <SummaryCard icon={CalendarClock} label="Próxima fatura vence em">
            {query.data?.nextDueDate ? formatDateBR(query.data.nextDueDate) : '—'}
          </SummaryCard>
          <SummaryCard icon={Wallet} label="Limite disponível">
            <MoneyText cents={query.data?.totalAvailableCents ?? 0} bold className="text-lg" />
          </SummaryCard>
          <SummaryCard icon={ReceiptText} label="Valor total" className="col-span-2 xl:col-span-1">
            <MoneyText cents={query.data?.totalDueCents ?? 0} bold className="text-lg" />
          </SummaryCard>
        </aside>
      </div>

      <CardFormModal open={formOpen} onClose={() => setFormOpen(false)} card={editing} />
      {paying && (
        <PayInvoiceModal
          open={!!paying}
          onClose={() => setPaying(null)}
          cardId={paying.id}
          cardName={paying.name}
          invoiceMonth={paying.invoice.invoiceMonth}
          remainingCents={paying.invoice.remainingCents}
          linkedAccountId={paying.linkedAccountId}
        />
      )}
    </section>
  );
}
